import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ChangePasswordDialog from './ChangePasswordDialog';
import { 
  LayoutDashboard, 
  Package, 
  Boxes, 
  ArrowRightLeft, 
  Wrench, 
  FileText, 
  Users, 
  LogOut, 
  Menu, 
  Tags,
  Building2,
  Plus,
  Truck,
  Briefcase,
  BarChart3,
  TrendingUp,
  Receipt,
  KeyRound,
  Settings,
  SlidersHorizontal
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'technician'] },
  { path: '/inventory', label: 'Inventory', icon: Boxes, roles: ['admin', 'manager', 'technician'] },
  { path: '/items', label: 'Items', icon: Package, roles: ['admin', 'manager'] },
  { path: '/restock', label: 'Restock', icon: Plus, roles: ['admin', 'manager'] },
  { path: '/usage', label: 'Record Usage', icon: Wrench, roles: ['admin', 'manager', 'technician'] },
  { path: '/transfer', label: 'Transfer', icon: ArrowRightLeft, roles: ['admin', 'manager'] },
  { path: '/job-summary', label: 'Job Costs', icon: Briefcase, roles: ['admin', 'manager', 'technician'] },
  { path: '/purchases', label: 'Purchases', icon: Receipt, roles: ['admin', 'manager'] },
  { path: '/reports', label: 'Reports', icon: FileText, roles: ['admin', 'manager'] },
  { path: '/vendor-analytics', label: 'Vendor Analytics', icon: TrendingUp, roles: ['admin', 'manager'] },
  { path: '/item-analytics', label: 'Item Analytics', icon: BarChart3, roles: ['admin', 'manager'] },
];

const adminItems = [
  { path: '/admin/vendors', label: 'Vendors', icon: Truck, roles: ['admin', 'manager'] },
  { path: '/admin/users', label: 'Users', icon: Users, roles: ['admin'] },
  { path: '/admin/categories', label: 'Categories', icon: Tags, roles: ['admin', 'manager'] },
  { path: '/admin/brands', label: 'Brands', icon: Building2, roles: ['admin', 'manager'] },
  { path: '/admin/inventories', label: 'Inventories', icon: Boxes, roles: ['admin', 'manager'] },
  { path: '/admin/data', label: 'Data Management', icon: Settings, roles: ['admin'] },
  { path: '/admin/settings', label: 'Settings', icon: SlidersHorizontal, roles: ['admin'] },
];

const NavLink = ({ item, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  const Icon = item.icon;
  
  return (
    <Link
      to={item.path}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg mx-2 transition-all
        ${isActive 
          ? 'bg-white/15 text-white' 
          : 'text-white/70 hover:bg-white/10 hover:text-white'
        }`}
    >
      <Icon size={20} strokeWidth={1.5} />
      <span className="tracking-wide">{item.label}</span>
    </Link>
  );
};

const Sidebar = ({ user, onLogout, onChangePassword }) => {
  const filteredNavItems = navItems.filter(item => item.roles.includes(user.role));
  const filteredAdminItems = adminItems.filter(item => item.roles.includes(user.role));
  
  return (
    <aside className="fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-[#0776BD] to-[#055A92] text-white hidden md:flex flex-col z-50 shadow-lg print:!hidden">
      <div className="p-4 border-b border-white/10">
        <Link to="/dashboard" className="flex flex-col items-center" data-testid="logo-link">
          <img 
            src="/mr-electric-logo.jpg" 
            alt="Mr. Electric" 
            className="h-16 w-auto object-contain rounded bg-white p-1"
          />
          <p className="text-xs text-white/60 tracking-wide mt-2">Inventory Control</p>
        </Link>
      </div>
      
      <nav className="flex-1 py-4 overflow-y-auto">
        <div className="space-y-1">
          {filteredNavItems.map(item => (
            <NavLink key={item.path} item={item} />
          ))}
        </div>
        
        {filteredAdminItems.length > 0 && (
          <>
            <div className="mt-6 mb-2 px-4">
              <p className="text-xs text-white/40 uppercase tracking-wider font-medium">Administration</p>
            </div>
            <div className="space-y-1">
              {filteredAdminItems.map(item => (
                <NavLink key={item.path} item={item} />
              ))}
            </div>
          </>
        )}
      </nav>
      
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3 px-2">
          <div className="w-10 h-10 bg-[#F0C500] rounded-full flex items-center justify-center shadow-sm">
            <span className="text-[#1F2937] font-bold">{user.name.charAt(0).toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user.name}</p>
            <p className="text-xs text-white/60 uppercase">{user.role === 'technician' ? 'Service Professional' : user.role}</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10 rounded-lg mb-1"
          onClick={onChangePassword}
          data-testid="change-password-btn"
        >
          <KeyRound size={18} className="mr-2" />
          Change Password
        </Button>
        <Button 
          variant="ghost" 
          className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
          onClick={onLogout}
          data-testid="logout-btn"
        >
          <LogOut size={18} className="mr-2" />
          Sign Out
        </Button>
      </div>
    </aside>
  );
};

const MobileNav = ({ user, onLogout, onChangePassword }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const filteredNavItems = navItems.filter(item => item.roles.includes(user.role));
  const filteredAdminItems = adminItems.filter(item => item.roles.includes(user.role));
  
  return (
    <>
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-[#0776BD] flex items-center justify-between px-4 z-40 md:hidden shadow-md">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="mobile-logo">
          <img 
            src="/mr-electric-logo.jpg" 
            alt="Mr. Electric" 
            className="h-10 w-auto object-contain rounded bg-white p-0.5"
          />
        </Link>
        
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="mobile-menu-btn">
              <Menu size={24} />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 bg-gradient-to-b from-[#0776BD] to-[#055A92] text-white p-0 border-0">
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F0C500] rounded-full flex items-center justify-center">
                  <span className="text-[#1F2937] font-bold">{user.name.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <p className="font-medium">{user.name}</p>
                  <p className="text-xs text-white/60 uppercase">{user.role === 'technician' ? 'Service Professional' : user.role}</p>
                </div>
              </div>
            </div>
            
            <nav className="py-4">
              {filteredNavItems.map(item => (
                <NavLink key={item.path} item={item} onClick={() => setOpen(false)} />
              ))}
              
              {filteredAdminItems.length > 0 && (
                <>
                  <div className="mt-4 mb-2 px-4">
                    <p className="text-xs text-white/40 uppercase tracking-wider">Admin</p>
                  </div>
                  {filteredAdminItems.map(item => (
                    <NavLink key={item.path} item={item} onClick={() => setOpen(false)} />
                  ))}
                </>
              )}
            </nav>
            
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10 mb-1"
                onClick={() => { onChangePassword(); setOpen(false); }}
                data-testid="mobile-change-password-btn"
              >
                <KeyRound size={18} className="mr-2" />
                Change Password
              </Button>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => { onLogout(); setOpen(false); }}
                data-testid="mobile-logout-btn"
              >
                <LogOut size={18} className="mr-2" />
                Sign Out
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </header>
      
      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-gray-200 flex items-center justify-around z-40 md:hidden shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.08)]">
        {filteredNavItems.slice(0, 5).map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex flex-col items-center gap-1 px-3 py-2 transition-colors
                ${isActive ? 'text-[#0776BD]' : 'text-gray-500'}`}
            >
              <Icon size={22} strokeWidth={isActive ? 2 : 1.5} />
              <span className="text-[10px] font-medium tracking-wide">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </>
  );
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  return (
    <div className="min-h-screen bg-[#F5F6F7]">
      <Sidebar user={user} onLogout={handleLogout} onChangePassword={() => setChangePasswordOpen(true)} />
      <MobileNav user={user} onLogout={handleLogout} onChangePassword={() => setChangePasswordOpen(true)} />
      
      <main className="md:ml-64 pt-16 md:pt-0 pb-20 md:pb-0 min-h-screen">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
};
